// frontend/src/hooks/useSyncStatus.js
//
// The selected store's sync progress (`GET /api/store/sync-status/`), read by
// the entry order (`components/entry/entryScreen.js`): a merchant without
// access whose price waits on the import sees it in front of the paywall.
//
// One TanStack query per store (`queries/keys.syncStatus`), seeded by the
// entry read (`hooks/useEntryStatus`) and polled from there at the interval
// `utils/syncGate.syncPollInterval` names. `syncStatusQueryOptions` is
// the query itself, exported so the readers inside the app
// (`hooks/useInAppSyncStatus`: the sidebar's `SyncProgressPill`, the Home
// tiles) subscribe to the same key and TanStack dedupes the read.
//
// A failed read is reported as `isError`, never as "no sync": `hasSync:
// false` is also what a store with nothing imported yet looks like, and the
// entry screen fails closed on it (`components/entry/entryScreen.js`).

import { useQuery } from '@tanstack/react-query';

import { apiRequest } from '../services/SessionApiService';
import { syncStatus as syncStatusKey } from '../queries/keys';
import { priceWaitsOnImport, syncPollInterval } from '../utils/syncGate';

/**
 * The sync-status query for one store: key, fetch and poll rate. Every
 * subscriber passes the same options so there is one poll loop per store.
 *
 * @param {string|null} storeId  identity of the selected store.
 * @param {{accessDenied?: boolean}} [context]  see `syncPollInterval`.
 */
export function syncStatusQueryOptions(storeId, { accessDenied = false } = {}) {
  return {
    queryKey: syncStatusKey.all(storeId),
    queryFn: async () => {
      const response = await apiRequest('/api/store/sync-status/');
      if (!response.ok) throw new Error(`sync-status failed: HTTP ${response.status}`);
      return response.json();
    },
    // Install progress is the definition of volatile — never serve it from
    // cache without revalidating, whatever the client-wide default is.
    staleTime: 0,
    refetchInterval: ({ state }) => syncPollInterval(state.data, { accessDenied }),
  };
}

/**
 * @param {Object} options
 * @param {boolean} options.applies  true once the entry read has seeded this
 *   store's query; false when there is nothing to read (no Shopify store
 *   selected, the entry read not yet answered or its sync part failed) —
 *   nothing is fetched and `isLoading` stays false; the caller must not gate
 *   on it.
 * @param {string|null} options.storeId  identity of the selected store.
 * @param {boolean} [options.accessDenied]  the merchant is held at the
 *   paywall: the price waits on the import (`priceWaitsOnImport`), and the
 *   orders landing after the run completed are polled for.
 */
export function useSyncStatus({ applies, storeId, accessDenied = false }) {
  const enabled = applies && !!storeId;
  // The seed is the server's answer of a moment ago: fresh until the poll
  // replaces it, so enabling the query does not fetch the same payload twice.
  // A merchant tabbing back still gets a fresh read ('always' ignores
  // staleness), as every other query does on focus.
  const query = useQuery({
    ...syncStatusQueryOptions(storeId, { accessDenied }),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: 'always',
  });

  const syncStatus = query.data ?? null;

  return {
    syncStatus,
    isLoading: enabled && query.isPending,
    isError: query.isError,
    refetch: query.refetch,
    priceWaitsOnImport: syncStatus !== null && priceWaitsOnImport(syncStatus),
  };
}

export default useSyncStatus;
